import {useCallback, useEffect, useState, useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {repositoryDefaultBranchPath} from '@github-ui/paths'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat/utils/copilot-chat-types'

export interface DropdownRepository extends AgentDropdownRepository {}

export interface SimpleRepository {
  name: string
  defaultBranch?: string // Optional default branch if available from repository data
  owner: {
    login: string
  }
}

type InitialRepoApiMetadata = Pick<DropdownRepository, 'private' | 'graphQLId' | 'isEmpty'>

/**
 * Fetch default branch for a specific repository using the GitHub repository path
 * This is more efficient than using the search API since it targets a specific repo directly
 */
export async function fetchRepositoryDefaultBranch(nameWithOwner: string): Promise<string | null> {
  try {
    const [owner, repo] = nameWithOwner.split('/')
    if (!owner || !repo) {
      return null
    }

    const url = repositoryDefaultBranchPath({owner, repo})
    const response = await verifiedFetchJSON(url)

    if (response.ok) {
      const data = await response.json()

      if (!data) return null

      return data?.defaultBranch || null
    }

    return null
  } catch {
    // Silently fail if fetching default branch fails, we'll get the branch name
    // From the selected repository's defaultBranch property if available
    return null
  }
}

/**
 * Hook to fetch repository default branch with React Query caching
 * This provides better caching, deduplication, and loading states
 */
export function useRepositoryDefaultBranch(nameWithOwner: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['repository-default-branch', nameWithOwner],
    queryFn: () => fetchRepositoryDefaultBranch(nameWithOwner ?? ''),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Only retry once on failure
  })
}

/**
 * Hook to manage repository fetching and selection logic for Copilot assignment forms
 */
export function useCopilotRepositoryFetching(
  initialRepo: SimpleRepository,
  initialOverrides?: {selectedRepo?: DropdownRepository; selectedBranch?: string},
) {
  // Memoize the initialRepo to prevent unnecessary re-renders when parent passes unstable object references
  const stableInitialRepo = useMemo(
    () => ({
      name: initialRepo.name,
      defaultBranch: initialRepo.defaultBranch,
      owner: {login: initialRepo.owner.login},
    }),
    [initialRepo.name, initialRepo.defaultBranch, initialRepo.owner.login],
  )

  // Seed repo/branch from overrides (persisted from previous modal) when available
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(initialOverrides?.selectedRepo ?? null)
  const [selectedBranch, setSelectedBranch] = useState<string>(initialOverrides?.selectedBranch ?? '')
  const [isLoadingBranch, setIsLoadingBranch] = useState(false)
  const [initialRepoApiMetadata, setInitialRepoApiMetadata] = useState<InitialRepoApiMetadata | null>(null)

  // Callback to update initial repo with full data from ReposSelector onLoad
  const handleReposLoaded = useCallback(
    (repos: DropdownRepository[]) => {
      if (initialRepoApiMetadata) return

      const nameWithOwner = `${stableInitialRepo.owner.login}/${stableInitialRepo.name}`
      const matchingRepo = repos.find(r => r.nameWithOwner === nameWithOwner)

      if (!matchingRepo) return

      const fetchedAttributes = {
        private: matchingRepo.private,
        isEmpty: matchingRepo.isEmpty,
        graphQLId: matchingRepo.graphQLId,
      }

      setInitialRepoApiMetadata(fetchedAttributes)

      // Update the initial selectedRepo with real data from API
      setSelectedRepo(prev => {
        if (prev?.nameWithOwner === nameWithOwner) {
          return {...prev, ...fetchedAttributes}
        }
        return prev
      })
    },
    [stableInitialRepo, initialRepoApiMetadata],
  )

  // Use React Query to fetch default branch with caching
  const nameWithOwner = `${stableInitialRepo.owner.login}/${stableInitialRepo.name}`
  // Skip fetching default branch if overrides already provide one
  const shouldFetchDefaultBranch = !stableInitialRepo.defaultBranch && !initialOverrides?.selectedBranch
  const {data: defaultBranchFromQuery, isLoading: isLoadingFromQuery} = useRepositoryDefaultBranch(
    nameWithOwner,
    shouldFetchDefaultBranch,
  )

  // Initialize repo from props — only if user hasn't picked a repo yet
  useEffect(() => {
    if (selectedRepo) return

    const repoNameWithOwner = `${stableInitialRepo.owner.login}/${stableInitialRepo.name}`

    // Set the repo immediately
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedRepo({
      owner: stableInitialRepo.owner.login,
      name: repoNameWithOwner,
      nameOnly: stableInitialRepo.name,
      nameWithOwner: repoNameWithOwner,
      enabled: true,
      defaultBranch: stableInitialRepo.defaultBranch || '',
      // Will be updated on initial repo fetch
      private: false,
      graphQLId: '',
      isEmpty: false,
    })

    // Set the branch immediately if we have defaultBranch from GraphQL data
    if (stableInitialRepo.defaultBranch) {
      setSelectedBranch(stableInitialRepo.defaultBranch)
    }
  }, [stableInitialRepo, selectedRepo])

  // Update loading state from React Query
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoadingBranch(isLoadingFromQuery)
  }, [isLoadingFromQuery])

  // Update branch when React Query returns data — skip if overrides are present
  useEffect(() => {
    if (defaultBranchFromQuery && !stableInitialRepo.defaultBranch && !initialOverrides?.selectedBranch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedBranch(defaultBranchFromQuery)
      setSelectedRepo(prev => (prev ? {...prev, defaultBranch: defaultBranchFromQuery} : prev))
    }
  }, [defaultBranchFromQuery, stableInitialRepo.defaultBranch, initialOverrides?.selectedBranch])

  const handleRepoSelect = useCallback((repo: DropdownRepository | undefined) => {
    if (!repo) return

    // Reset loading state since we're setting the branch immediately from repo.defaultBranch
    setIsLoadingBranch(false)
    setSelectedRepo(repo)
    setSelectedBranch(repo.defaultBranch)
  }, [])

  return {
    selectedRepo,
    selectedBranch,
    isLoadingBranch,
    initialRepoApiMetadata,
    setSelectedBranch,
    handleRepoSelect,
    handleReposLoaded,
  }
}
